import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { EllipsisVertical, Users, Plus, ArrowLeft, CheckCircle, UserPlus } from 'lucide-react';
import { useTeam, type TeamMember } from '@/hooks/useTeam';
import { useCheckEmail, type CheckEmailResult } from '@/hooks/useCheckEmail';
import { useAuth } from '@/contexts/AuthContext';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';

interface AreaPermissions {
  dashboard: boolean;
  contatos: boolean;
  agenda: boolean;
  crm: boolean;
  chat: boolean;
  campanhas: boolean;
  assistentes: boolean;
  configuracoes: boolean;
}

const TeamTab: React.FC = () => {
  const { user } = useAuth();
  const { members, loading, error, createMember, updateMember, deleteMember } = useTeam();
  const { checkEmail, checking } = useCheckEmail();

  // Dialog e step control
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState<'email' | 'details'>('email');

  // User check results
  const [checkResult, setCheckResult] = useState<CheckEmailResult | null>(null);

  // Editing
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  // Form states
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    is_active: true,
    send_email: true
  });
  const [permissions, setPermissions] = useState<AreaPermissions>({
    dashboard: false,
    contatos: false,
    agenda: false,
    crm: false,
    chat: false,
    campanhas: false,
    assistentes: false,
    configuracoes: false
  });

  const areaLabels: Record<keyof AreaPermissions, string> = {
    dashboard: 'Dashboard',
    contatos: 'Contatos',
    agenda: 'Agenda',
    crm: 'CRM',
    chat: 'Chat',
    campanhas: 'Campanhas',
    assistentes: 'Assistentes',
    configuracoes: 'Configurações'
  };

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePassword = (password: string): boolean => {
    return password.length >= 6;
  };

  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateEmail(formData.email)) {
      setSubmitError('Email inválido');
      return;
    }

    if (!user?.account?.id) {
      setSubmitError('Conta não encontrada');
      return;
    }

    const result = await checkEmail(formData.email, user.account.id);

    if (!result) {
      setSubmitError('Erro ao verificar email. Tente novamente.');
      return;
    }

    if (result.alreadyInAccount) {
      setSubmitError('Este usuário já está cadastrado nesta conta');
      return;
    }

    setCheckResult(result);

    // Se usuário existe, preencher nome
    if (result.exists && result.user?.name) {
      setFormData(prev => ({ ...prev, name: result.user!.name || '' }));
    }

    setStep('details');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!editingMember) {
      // Validar senha apenas para novos usuários
      if (!checkResult?.exists && !validatePassword(formData.password)) {
        setSubmitError('Senha deve ter pelo menos 6 caracteres');
        return;
      }
    }

    const roleData = JSON.stringify(permissions);

    try {
      setSubmitting(true);

      if (editingMember) {
        const dataToUpdate = {
          name: formData.name || null,
          phone: formData.phone || null,
          role: roleData,
          is_active: formData.is_active
        };

        const success = await updateMember(editingMember.id, dataToUpdate);
        if (success) {
          setDialogOpen(false);
          resetForm();
        }
      } else {
        const created = await createMember({
          email: formData.email,
          password: formData.password,
          name: formData.name || null,
          phone: formData.phone || null,
          role: roleData,
          is_active: formData.is_active,
          send_email: formData.send_email
        });
        if (created) {
          setDialogOpen(false);
          resetForm();
        }
      }
    } catch (err: any) {
      setSubmitError(err.message || 'Erro ao salvar membro');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      email: member.email,
      password: '',
      name: member.name || '',
      phone: member.phone || '',
      is_active: member.is_active,
      send_email: false
    });

    try {
      const parsedPermissions = member.role ? JSON.parse(member.role) : {};
      setPermissions({
        dashboard: parsedPermissions.dashboard || false,
        contatos: parsedPermissions.contatos || false,
        agenda: parsedPermissions.agenda || false,
        crm: parsedPermissions.crm || false,
        chat: parsedPermissions.chat || false,
        campanhas: parsedPermissions.campanhas || false,
        assistentes: parsedPermissions.assistentes || false,
        configuracoes: parsedPermissions.configuracoes || false
      });
    } catch {
      setPermissions({
        dashboard: false,
        contatos: false,
        agenda: false,
        crm: false,
        chat: false,
        campanhas: false,
        assistentes: false,
        configuracoes: false
      });
    }

    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja remover este membro da equipe?')) {
      await deleteMember(id);
    }
  };

  const resetForm = () => {
    setEditingMember(null);
    setSubmitError(null);
    setStep('email');
    setCheckResult(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      phone: '',
      is_active: true,
      send_email: true
    });
    setPermissions({
      dashboard: false,
      contatos: false,
      agenda: false,
      crm: false,
      chat: false,
      campanhas: false,
      assistentes: false,
      configuracoes: false
    });
  };

  const toggleAllPermissions = (checked: boolean) => {
    setPermissions({
      dashboard: checked,
      contatos: checked,
      agenda: checked,
      crm: checked,
      chat: checked,
      campanhas: checked,
      assistentes: checked,
      configuracoes: checked
    });
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  const getPermissionsSummary = (roleString: string | null): string => {
    if (!roleString) return 'Sem permissões';

    try {
      const perms = JSON.parse(roleString);
      const active = Object.entries(perms).filter(([_, value]) => value === true);
      if (active.length === 0) return 'Sem permissões';
      if (active.length === 8) return 'Todas as áreas';
      return `${active.length} ${active.length === 1 ? 'área' : 'áreas'}`;
    } catch {
      return 'Sem permissões';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-neutral-50">Equipe</h2>
          <p className="text-sm text-neutral-400">Gerencie os membros da sua equipe</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-neutral-400">
            <Users className="w-5 h-5" />
            <span className="text-sm">{members.length} {members.length === 1 ? 'membro' : 'membros'}</span>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] text-neutral-900 hover:bg-[#D4D4D8]">
                <Plus className="w-4 h-4 mr-2" />
                Novo Membro
              </Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col max-w-[500px]">
              <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {editingMember ? 'Editar Membro' :
                      step === 'email' ? 'Adicionar Membro - Etapa 1 de 2' :
                      'Adicionar Membro - Etapa 2 de 2'}
                  </DialogTitle>
                  {!editingMember && (
                    <p className="text-sm text-neutral-400 mt-2">
                      {step === 'email' ? 'Digite o email do membro' :
                        checkResult?.exists ? 'Associar usuário existente' : 'Cadastrar novo membro'}
                    </p>
                  )}
                </DialogHeader>

                {/* STEP 1: Email Verification (only for new members) */}
                {!editingMember && step === 'email' && (
                  <form onSubmit={handleCheckEmail} className="w-full mt-4 gap-4 flex flex-col">
                    {submitError && (
                      <div className="p-3 rounded bg-red-900/20 border border-red-900/50 text-red-400 text-sm">
                        {submitError}
                      </div>
                    )}

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="email">Email do Membro</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                        placeholder="email@exemplo.com"
                        required
                        disabled={checking}
                      />
                      <p className="text-xs text-neutral-400">
                        Vamos verificar se este email já existe no sistema
                      </p>
                    </div>

                    <div className="flex gap-2 justify-end mt-4">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={checking}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="bg-[#E4E4E7] text-neutral-900" disabled={checking}>
                        {checking ? 'Verificando...' : 'Próximo'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* STEP 2: Details Form (or Edit Form) */}
                {(editingMember || step === 'details') && (
                  <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                    {submitError && (
                      <div className="p-3 rounded bg-red-900/20 border border-red-900/50 text-red-400 text-sm">
                        {submitError}
                      </div>
                    )}
                    {error && (
                      <div className="p-3 rounded bg-red-900/20 border border-red-900/50 text-red-400 text-sm">
                        {error}
                      </div>
                    )}

                    {/* User Status Badge (only for new members) */}
                    {!editingMember && checkResult && (
                      <div className={`p-3 rounded border ${
                        checkResult.exists
                          ? 'bg-green-900/20 border-green-900/50 text-green-400'
                          : 'bg-blue-900/20 border-blue-900/50 text-blue-400'
                      }`}>
                        <div className="flex items-center gap-2">
                          {checkResult.exists ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              <span className="font-medium">Usuário Encontrado</span>
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4" />
                              <span className="font-medium">Novo Usuário</span>
                            </>
                          )}
                        </div>
                        <p className="text-xs mt-1">
                          {checkResult.exists
                            ? 'Este usuário já possui cadastro e será associado à sua equipe.'
                            : 'Um novo usuário será criado e adicionado à sua equipe.'}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={editingMember ? editingMember.email : formData.email}
                        disabled
                        className="text-[14px] min-h-[36px] border bg-neutral-700 text-neutral-400 border-[#3F3F46] cursor-not-allowed"
                      />
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="name">Nome {!editingMember && '(opcional)'}</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Digite o nome"
                        className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      />
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="phone">Telefone</Label>
                      <Input
                        id="phone"
                        value={(() => {
                          const v = formData.phone;
                          if (!v) return '';
                          if (v.length <= 2) return `(${v}`;
                          if (v.length <= 7) return `(${v.slice(0,2)}) ${v.slice(2)}`;
                          if (v.length <= 10) return `(${v.slice(0,2)}) ${v.slice(2,6)}-${v.slice(6)}`;
                          return `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7,11)}`;
                        })()}
                        onChange={(e) => {
                          const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 11);
                          setFormData({ ...formData, phone: onlyNumbers });
                        }}
                        placeholder="(11) 99999-9999"
                        className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                        type="tel"
                        inputMode="numeric"
                      />
                    </div>

                    {/* Password field only for new users */}
                    {!editingMember && !checkResult?.exists && (
                      <div className="flex flex-col w-full items-start gap-3">
                        <Label htmlFor="password">Senha</Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                          placeholder="Digite uma senha"
                          required
                          minLength={6}
                        />
                        <p className="text-xs text-neutral-400">Mínimo 6 caracteres</p>
                      </div>
                    )}

                    <div className="flex flex-col w-full items-start gap-3 pt-4 border-t border-[#3F3F464D]">
                      <div className="flex items-center justify-between w-full">
                        <Label>Permissões de Acesso</Label>
                        <button
                          type="button"
                          onClick={() => toggleAllPermissions(!Object.values(permissions).every(v => v))}
                          className="text-sm text-blue-400 hover:text-blue-300"
                        >
                          {Object.values(permissions).every(v => v) ? 'Desmarcar todas' : 'Selecionar todas'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 w-full">
                        {(Object.keys(permissions) as Array<keyof AreaPermissions>).map((area) => (
                          <div key={area} className="flex items-center gap-2">
                            <Checkbox
                              id={area}
                              checked={permissions[area]}
                              onCheckedChange={(checked) => setPermissions({ ...permissions, [area]: !!checked })}
                            />
                            <Label htmlFor={area} className="cursor-pointer text-sm">
                              {areaLabels[area]}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-4 border-t border-[#3F3F464D]">
                      <Checkbox
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                      />
                      <Label htmlFor="is_active" className="cursor-pointer">Membro ativo</Label>
                    </div>

                    {/* Send email checkbox (only for new members) */}
                    {!editingMember && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="send_email"
                          checked={formData.send_email}
                          onCheckedChange={(checked) => setFormData({ ...formData, send_email: !!checked })}
                        />
                        <Label htmlFor="send_email" className="cursor-pointer text-sm">
                          {checkResult?.exists
                            ? 'Enviar email de notificação'
                            : 'Enviar dados de acesso por email'}
                        </Label>
                      </div>
                    )}

                    <div className="flex gap-2 justify-end mt-4">
                      {!editingMember && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep('email')}
                          disabled={submitting}
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Voltar
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        disabled={submitting}
                      >
                        Cancelar
                      </Button>
                      <Button type="submit" className="bg-[#E4E4E7] text-neutral-900" disabled={submitting || loading}>
                        {submitting ? 'Salvando...' : (editingMember ? 'Salvar' : checkResult?.exists ? 'Associar' : 'Cadastrar')}
                      </Button>
                    </div>
                  </form>
                )}
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-[#3F3F464D] rounded-lg bg-[#18181B]">
        {loading ? (
          <div className="p-8 text-center text-neutral-400">Carregando...</div>
        ) : members.length === 0 ? (
          <div className="p-8 text-center text-neutral-400">Nenhum membro na equipe</div>
        ) : (
          <div className="divide-y divide-[#3F3F464D]">
            {members.map((member) => (
              <div
                key={member.id}
                className="p-4 flex items-center justify-between hover:bg-neutral-800/50"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {getInitials(member.name, member.email)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-neutral-50">
                        {member.name || member.email}
                      </h3>
                      {!member.is_active && (
                        <span className="text-xs px-2 py-1 rounded bg-red-900/30 text-red-500">
                          Inativo
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-400">{member.email}</p>
                    {member.phone && (
                      <p className="text-sm text-neutral-400">
                        {member.phone.length === 11
                          ? `(${member.phone.slice(0,2)}) ${member.phone.slice(2,7)}-${member.phone.slice(7)}`
                          : member.phone.length === 10
                          ? `(${member.phone.slice(0,2)}) ${member.phone.slice(2,6)}-${member.phone.slice(6)}`
                          : member.phone}
                      </p>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded bg-neutral-700 text-neutral-300 inline-block mt-1">
                      {getPermissionsSummary(member.role)}
                    </span>
                  </div>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex justify-center items-center rounded-[8px] border-[1px] border-[#3F3F464D] bg-neutral-600 w-[34px] h-[34px]">
                      <EllipsisVertical size={20} color="#FAFAFA" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!bg-[#18181B] p-1 w-[95px] flex flex-col gap-1 border-[#3F3F464D] border">
                    <button
                      onClick={() => handleEdit(member)}
                      className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] text-red-400"
                    >
                      Remover
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamTab;
